/**
 * <cw-shell>: the basket bar, the offline strip and the tabs (phone and tablet).
 * design-handoff/components/basket-bar-tabs.md, toast-offline.md, behaviour.md §3.
 *
 * The bar's reward line reads Flow's delivery answer for the chosen address (cw-delivery.js):
 *   below free delivery           "Add ₹X for free delivery"
 *   free, express still to go     "Free delivery ✓ · ₹Y more for free express"   (express on)
 *   free express                  "Free express delivery unlocked ✓"  (gold)
 *   free, express switched off    "Free delivery unlocked ✓"
 * With express switched off there is one progress segment, not two. With no address there
 * is no line: the free-delivery bar depends on the distance, and guessing it would mislead.
 */
import { cart } from '@cw/cart';
import { view } from '@cw/delivery';

const rupees = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;

const FALLBACK = {
  basket: () => `${window.Shopify?.routes?.root || '/'}cart`,
  account: () => '/account',
  orderagain: () => '/account',
};

function reward(answer) {
  // Only for a real address: the free-delivery bar depends on the distance.
  if (!answer || answer.spot === 'origin' || !answer.serviceable || answer.free_over == null) return null;
  const value = Number(answer.value || 0);
  const free = Number(answer.free_over);
  const ex = answer.express || null;
  const exBar = ex ? Number(ex.free_over) : null;
  const segs = ex && exBar > free ? [free, exBar - free] : [free];
  const fill1 = Math.max(0, Math.min(1, value / free));
  const fill2 = segs.length > 1 ? Math.max(0, Math.min(1, (value - free) / (exBar - free))) : 0;
  if (Number(answer.to_free) > 0) {
    return { text: `Add ${rupees(answer.to_free)} for free delivery`, gold: false, segs, fills: [fill1, fill2] };
  }
  if (ex && !ex.free && exBar > value) {
    return { text: `Free delivery ✓ · ${rupees(exBar - value)} more for free express`, gold: false, segs, fills: [1, fill2] };
  }
  if (ex) return { text: 'Free express delivery unlocked ✓', gold: true, segs, fills: [1, 1] };
  return { text: 'Free delivery unlocked ✓', gold: false, segs, fills: [1] };
}

class CwShell extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('.cw-bar');
    this.offline = this.querySelector('.cw-offline');
    document.addEventListener('cw:cart:change', this.#basket);
    document.addEventListener('cw:delivery:change', this.#reward);
    document.addEventListener('cw:fold', this.#fold);
    document.addEventListener('cw:offline', this.#showOffline);
    document.addEventListener('cw:pdp:bar', this.#pdpBar);
    window.addEventListener('online', this.#network);
    window.addEventListener('offline', this.#network);
    this.addEventListener('click', this.#onClick);
    try {
      const last = sessionStorage.getItem('cw:last-aisle');
      if (last) this.querySelector('[data-cw="categories"]')?.setAttribute('href', last);
    } catch {
      // first aisle stays
    }
    this.#basket();
    this.#reward({ detail: view() });
    this.#network();
  }

  disconnectedCallback() {
    document.removeEventListener('cw:cart:change', this.#basket);
    document.removeEventListener('cw:delivery:change', this.#reward);
    document.removeEventListener('cw:fold', this.#fold);
    document.removeEventListener('cw:offline', this.#showOffline);
    document.removeEventListener('cw:pdp:bar', this.#pdpBar);
    window.removeEventListener('online', this.#network);
    window.removeEventListener('offline', this.#network);
    this.removeEventListener('click', this.#onClick);
  }

  #basket = () => {
    const n = cart.count;
    if (this.bar) this.bar.hidden = n === 0;
    this.classList.toggle('has-bar', n > 0);
    for (const el of this.querySelectorAll('[data-cw="count"]')) el.textContent = String(n);
    for (const el of this.querySelectorAll('[data-cw="items"]')) el.textContent = `${n} item${n === 1 ? '' : 's'}`;
  };

  #reward = (event) => {
    const line = this.querySelector('[data-cw="reward"]');
    const progress = this.querySelector('[data-cw="progress"]');
    if (!line || !progress) return;
    const r = reward(event.detail?.answer);
    line.hidden = !r;
    progress.hidden = !r;
    if (!r) return;
    line.textContent = r.text;
    line.classList.toggle('is-gold', r.gold);
    progress.querySelectorAll('.cw-bar__track').forEach((track, i) => {
      track.hidden = i >= r.segs.length;
      track.style.setProperty('--cw-seg', String(r.segs[i] || 1));
      track.querySelector('.cw-bar__fill').style.setProperty('--cw-fill', `${Math.round((r.fills[i] || 0) * 1000) / 10}%`);
    });
  };

  #fold = (event) => {
    this.classList.toggle('is-tabs-hidden', !!event.detail?.navHidden);
  };

  /** Product page, tablet: its own bar replaces this one once the ADD row has scrolled out. */
  #pdpBar = (event) => {
    this.classList.toggle('is-pdp-bar', !!event.detail?.shown);
  };

  #network = () => {
    if (this.offline) this.offline.hidden = navigator.onLine !== false;
  };

  #showOffline = () => {
    if (this.offline) this.offline.hidden = false;
  };

  #onClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-open]');
    if (!button || !this.contains(button)) return;
    const name = button.dataset.open;
    const request = new CustomEvent(`cw:${name}:open`, { cancelable: true, detail: { source: button } });
    document.dispatchEvent(request);
    if (!request.defaultPrevented && FALLBACK[name]) window.location.href = FALLBACK[name]();
  };
}

if (!customElements.get('cw-shell')) customElements.define('cw-shell', CwShell);

/*
 * A data-open button that no part of the page answers for itself: the product page's own bar
 * (its basket), the footer's Order Again. The header, the bottom bar, the rail, Home's sections
 * and search each answer their own; any other gets the same request here, so a button can never
 * be dead (owner, 2 Oct: the basket did not open from the product page). A link keeps its href
 * when nothing takes the request.
 */
document.addEventListener('click', (event) => {
  const button = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-open]');
  if (!button || button.tagName === 'INPUT' || event.defaultPrevented) return;
  if (button.closest('cw-header, cw-shell, cw-rail, cw-search, cw-search-page, .cw-home-sec')) return;
  const name = button.dataset.open;
  const request = new CustomEvent(`cw:${name}:open`, { cancelable: true, detail: { source: button } });
  document.dispatchEvent(request);
  if (request.defaultPrevented) {
    event.preventDefault();
    return;
  }
  if (button.tagName !== 'A' && FALLBACK[name]) window.location.href = FALLBACK[name]();
});

/**
 * <cw-toast> (snippets/cw-toast.liquid): document event 'cw:toast' with detail
 * {text, undo?: () => void}, or {hide: true} to take it away. UNDO shows only when there is
 * something to undo. It hides after about 4 s, or at once on UNDO. On phones it sits above the
 * basket bar and tabs, by the same rule as the offline strip (assets/cw-shell.css).
 *
 * Over an open panel (Order Again's REORDER and due ADD toast from inside one) it has to be
 * above the panel and still take a tap: it is a popover, so it is drawn in the top layer, and
 * while a modal panel is open it sits inside that panel, because everything outside a modal
 * dialog is inert.
 */
class CwToast extends HTMLElement {
  #timer = 0;
  #undo = null;
  /** Modal sheets open now, the newest last. */
  #modals = [];

  connectedCallback() {
    this.box = this.querySelector('.cw-toast__box');
    this.text = this.querySelector('[data-cw="text"]');
    this.undoButton = this.querySelector('[data-cw="undo"]');
    document.addEventListener('cw:toast', this.#show);
    document.addEventListener('cw:sheet:open', this.#onSheetOpen);
    document.addEventListener('cw:sheet:close', this.#onSheetClose);
    this.undoButton?.addEventListener('click', this.#onUndo);
  }

  disconnectedCallback() {
    document.removeEventListener('cw:toast', this.#show);
    document.removeEventListener('cw:sheet:open', this.#onSheetOpen);
    document.removeEventListener('cw:sheet:close', this.#onSheetClose);
    this.undoButton?.removeEventListener('click', this.#onUndo);
    clearTimeout(this.#timer);
  }

  #onSheetOpen = (event) => {
    const dialog = event.detail?.sheet?.dialog;
    if (dialog?.matches(':modal')) this.#modals.push(dialog);
  };

  #onSheetClose = (event) => {
    const dialog = event.detail?.sheet?.dialog;
    this.#modals = this.#modals.filter((d) => d !== dialog && d.open);
    // Closing the panel it sat in: back home, and still showing if it was.
    if (this.box && dialog?.contains(this.box)) this.#place(!this.box.hidden);
  };

  /** Inside the top modal panel, if one is open; else in its own element. */
  #place(show) {
    const host = this.#modals.filter((d) => d.open).at(-1) || this;
    if (this.box.parentElement !== host) host.append(this.box);
    if (show && this.box.showPopover && !this.box.matches(':popover-open')) this.box.showPopover();
  }

  #show = (event) => {
    const { text = '', undo = null, hide = false } = event.detail || {};
    if (hide) return this.#hide();
    if (!this.box || !text) return;
    this.#undo = typeof undo === 'function' ? undo : null;
    this.text.textContent = text;
    this.undoButton.hidden = !this.#undo;
    this.box.hidden = false;
    // Shown again from the start (a second toast replacing the first comes out on top).
    if (this.box.hidePopover && this.box.matches(':popover-open')) this.box.hidePopover();
    this.#place(true);
    // Restart the entrance when a second toast replaces the first.
    this.box.classList.remove('is-in');
    void this.box.offsetWidth;
    this.box.classList.add('is-in');
    clearTimeout(this.#timer);
    this.#timer = setTimeout(this.#hide, 4000);
  };

  #hide = () => {
    clearTimeout(this.#timer);
    this.#undo = null;
    if (!this.box) return;
    if (this.box.hidePopover && this.box.matches(':popover-open')) this.box.hidePopover();
    this.box.hidden = true;
  };

  #onUndo = () => {
    const undo = this.#undo;
    this.#hide();
    if (undo) undo();
  };
}

if (!customElements.get('cw-toast')) customElements.define('cw-toast', CwToast);
